import { Eye, Flag, Lightbulb, Volume2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { player } from '../audio/player';
import type { RecordingResult } from '../audio/recorder';
import { canOfferRecording, micDeclined, noteMicProblem, undoMicDeclined, useMicAccess } from '../audio/useMic';
import { RoleChip } from '../components/Cast';
import { hasIllustration, Illustration } from '../components/Illustration';
import { MicNote } from '../components/MicNote';
import { StepHead } from '../components/StepHead';
import { NoMicPanel } from '../components/NoMicPanel';
import { Compare } from '../components/Compare';
import { FlagSheet } from '../components/FlagSheet';
import { GradeBar } from '../components/GradeBar';
import { GreekText } from '../components/GreekText';
import { PlayButton } from '../components/PlayButton';
import { RecordControl, type MicProblem } from '../components/RecordControl';
import { toast } from '../components/Toast';
import { frameOf, getItem, roleName, staffOf } from '../content';
import type { Item } from '../content/types';
import { firstLetterHint, halfHint } from '../lib/greek';
import type { CardRecord, Grade } from '../lib/scheduler';
import { clipFor, itemVoice, sceneVoice, speechRange, VOICE_LABEL, voicesOf } from '../lib/voice';
import { grade, presented, useAppState } from '../state/store';
import { WhyNote } from './WhyNote';

interface Props {
  card: CardRecord;
  item: Item;
  mode: 'first' | 'review' | 'repeat';
  onDone: () => void;
  onFlagged: () => void;
}

/** Cards forgotten this often get extra support: a marker and the model played slowly. */
const TRICKY_LAPSES = 3;

/**
 * The say-it card: the English cue, the learner says the Greek aloud (recorded if they like),
 * then the model, their recording next to it, and a self-grade against a written rubric.
 * Once a phrase has been reviewed, every other showing starts with the other person's line it answers,
 * played aloud, so the learner practises replying to what they hear.
 */
export function SayStep({ card, item, mode, onDone, onFlagged }: Props) {
  const { settings, profile } = useAppState();
  const [answer, setAnswer] = useState(false);
  const [recording, setRecording] = useState<RecordingResult | null>(null);
  const access = useMicAccess();
  const [refused, setRefused] = useState(micDeclined);
  const noMic = !canOfferRecording(access, refused);
  const retryMic = () => {
    undoMicDeclined();
    setRefused(false);
  };
  const onMicProblem = (problem: MicProblem) => {
    noteMicProblem(problem);
    setRefused(true);
  };
  const [hint, setHint] = useState<0 | 1 | 2>(0);
  // Try before you peek: the answer unlocks after three seconds of trying (or a recording).
  const [mayPeek, setMayPeek] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setMayPeek(true), 3000);
    return () => clearTimeout(t);
  }, [card.id]);
  const [cueWords, setCueWords] = useState(false);
  const [autoPlay, setAutoPlay] = useState(0);
  const [flagOpen, setFlagOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // The match of the recording made before the answer was shown: the recall attempt. Recordings
  // made afterwards are practice by imitation and aren't logged with the grade.
  const [recallUrl, setRecallUrl] = useState<string | null>(null);
  const recalled = recallUrl !== null;
  const [recallMatch, setRecallMatch] = useState<number | null | undefined>(undefined);
  const staff = staffOf(item);
  const voice = itemVoice(item, staff);
  const clip = clipFor(item, voice);
  const range = useMemo(() => speechRange(clip), [clip]);
  const frame = frameOf(item);
  const familiar = card.fsrs.reps >= 3 && card.fsrs.lapses === 0;
  const tricky = card.fsrs.lapses >= TRICKY_LAPSES;
  const cue = useMemo(
    () => (item.replyTo && mode === 'review' && card.fsrs.reps >= 1 && card.presentations % 2 === 1 ? getItem(item.replyTo) : null),
    [card.id], // eslint-disable-line react-hooks/exhaustive-deps
  );
  // The cue is the line of the person on this card, in their voice.
  const cueClip = cue ? clipFor(cue, sceneVoice(cue, 'staff', staff)) : null;
  // A picture cue only while the phrase is new (stability under a week) and only on every other
  // showing, so recall never comes to depend on the picture. It always returns with the answer.
  const pictureCue = useMemo(
    () => !cue && hasIllustration(item.id) && (mode === 'first' || card.fsrs.stability < 7) && card.presentations % 2 === 0,
    [card.id], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const playCue = () => {
    if (cueClip) player.play(cueClip.src, { ...speechRange(cueClip), tag: `cue:${card.id}` });
  };

  useEffect(() => {
    presented(card.id);
    const t = cueClip ? window.setTimeout(playCue, 250) : 0;
    return () => {
      clearTimeout(t);
      player.stop();
    };
  }, [card.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => {
    if (recording) URL.revokeObjectURL(recording.url);
  }, [recording]);

  const reveal = (r: RecordingResult | null) => {
    if (r) {
      setRecording(r);
      setRecallUrl(r.url);
    }
    setAnswer(true);
    setAutoPlay((n) => n + 1);
    if (!r) player.play(clip.src, { ...range, rate: tricky ? 0.8 : 1, tag: `say:${card.id}:model` });
  };

  /** Recording again (or for the first time) after seeing the answer: practice, compared like the rest. */
  const practise = (r: RecordingResult) => {
    setRecording(r);
    setAutoPlay((n) => n + 1);
  };

  const onMatch = (score: number | null, url: string) => {
    if (url === recallUrl) setRecallMatch(score);
  };

  const onGrade = async (value: Extract<Grade, 'missed' | 'got' | 'instant'>) => {
    if (busy) return;
    setBusy(true);
    player.stop();
    await grade(card.id, value, {
      repeat: mode === 'repeat',
      learning: mode === 'first',
      hint: hint > 0,
      recorded: recalled,
      match: recalled ? (recallMatch ?? null) : undefined,
    });
    if (value === 'missed' && mode !== 'repeat') toast("No problem. You'll see it once more today.");
    onDone();
  };

  return (
    <div className="step">
      <div className="stack">
        <StepHead kind="recall" role={<RoleChip staff={staff} from="you" />} why={profile.lessonsDone.length === 0 && mode === 'first'}>
          {mode === 'repeat' && <span className="chip chip-warn">One more time</span>}
          {tricky && <span className="chip chip-warn">Tricky one: take it slowly</span>}
        </StepHead>
        {pictureCue && <Illustration id={item.id} size="cue" priority className="cue-art" />}
        {cue && cueClip ? (
          <div className="bubble bubble-staff" style={{ maxWidth: '100%' }}>
            <span className="bubble-who">The {roleName(staff)} says</span>
            <div className="row" style={{ gap: 8 }}>
              <button type="button" className="icon-btn" onClick={playCue} aria-label={`Play the ${roleName(staff)}'s line again`}>
                <Volume2 size={18} aria-hidden="true" />
              </button>
              {cueWords ? (
                <div className="stack" style={{ gap: 2 }}>
                  <GreekText text={cue.text} size="body" />
                  <span className="small muted">{cue.meaning}</span>
                </div>
              ) : (
                <button type="button" className="btn btn-ghost small" onClick={() => setCueWords(true)}>
                  <Eye size={16} aria-hidden="true" /> Show words
                </button>
              )}
            </div>
          </div>
        ) : (
          item.context && <p className="context">{item.context}</p>
        )}
        <p className="eyebrow">{cue ? 'Answer with' : 'How do you say'}</p>
        <h2 className="english-cue">{item.meaning}</h2>
      </div>

      {!answer && (
        <div className="stack-lg">
          {hint > 0 && (
            <p className="reveal">
              <span className="sr-only" lang="el">
                Hint: {(hint === 1 ? firstLetterHint(item.text) : halfHint(item.text)).replace(/_+/g, ' …')}
              </span>
              <span className="greek greek-large" aria-hidden="true" style={{ letterSpacing: '0.06em', color: 'var(--ink-2)' }}>
                {hint === 1 ? firstLetterHint(item.text) : halfHint(item.text)}
              </span>
            </p>
          )}
          {hint < 2 && (
            <button type="button" className="btn btn-ghost" style={{ alignSelf: 'flex-start' }} onClick={() => setHint(hint === 0 ? 1 : 2)}>
              <Lightbulb size={18} aria-hidden="true" /> {hint === 0 ? 'Hint: first letters' : 'More of it'}
            </button>
          )}
          {noMic && <NoMicPanel title="Say it aloud first" body="Then check yourself against the answer." access={access} onRetry={retryMic} />}
        </div>
      )}

      {answer && (
        <div className="stack-lg reveal">
          {!pictureCue && <Illustration id={item.id} size="cue" decorative className="cue-art" />}
          <div className="stack" style={{ gap: 4 }}>
            <GreekText text={item.text} size="hero" ipa={item.ipa} showRespelling={settings.showRespelling} />
          </div>
          {recording ? (
            <Compare
              clip={clip}
              models={voicesOf(item).map((v) => clipFor(item, v))}
              voiceName={VOICE_LABEL[voice]}
              recording={recording}
              autoPlay={autoPlay}
              id={`say:${card.id}`}
              onMatch={onMatch}
              action={!noMic ? <RecordControl inline label="Record again" onResult={practise} onProblem={onMicProblem} /> : undefined}
            />
          ) : (
            <div className="stack">
              <div className="row-wrap">
                <PlayButton src={clip.src} tag={`say:${card.id}:model`} label="Listen" rate={tricky ? 0.8 : 1} from={range.from} to={range.to} />
                <PlayButton src={clip.src} tag={`say:${card.id}:slow`} label="Slowly" rate={0.75} from={range.from} to={range.to} />
              </div>
              {!noMic && <RecordControl inline label="Record yourself" onResult={practise} onProblem={onMicProblem} />}
            </div>
          )}
          <WhyNote item={item} frame={frame} open={!familiar} />
          <div className="row">
            <span className="spacer" />
            <button type="button" className="btn btn-ghost small" onClick={() => setFlagOpen(true)} aria-label="Flag this phrase as wrong">
              <Flag size={16} aria-hidden="true" /> Flag
            </button>
          </div>
        </div>
      )}

      <div className="actions">
        {!answer ? (
          <>
            {/* The microphone sits in the thumb's reach, with the way out under it. */}
            {!noMic && (
              <>
                <RecordControl label="Say it aloud" onResult={(r) => reveal(r)} onProblem={onMicProblem} />
                <MicNote access={access} />
              </>
            )}
            {!noMic && (
              <p className="small muted" style={{ textAlign: 'center' }} aria-live="polite">
                {mayPeek ? "Stuck? It's fine to look." : 'Say it aloud first, then check.'}
              </p>
            )}
            <button
              type="button"
              className={`btn btn-block ${noMic ? 'btn-primary btn-lg' : 'btn-ghost'}${!mayPeek && !noMic ? ' btn-wait' : ''}`}
              onClick={() => reveal(null)}
              disabled={!mayPeek && !noMic}
            >
              <Eye size={20} aria-hidden="true" /> Show answer
            </button>
          </>
        ) : (
          <GradeBar onGrade={onGrade} disabled={busy} noInstant={hint > 0 ? 'hint' : !recalled && !noMic ? 'unrecorded' : false} />
        )}
      </div>

      <FlagSheet itemId={item.id} open={flagOpen} onClose={() => setFlagOpen(false)} onFlagged={onFlagged} />
    </div>
  );
}
